import React, { useState, useEffect } from 'react'
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react'
import { XMarkIcon, CheckCircleIcon, LockClosedIcon, TicketIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_BOAT_ROUTE_SEAT_AVAILABILITY } from 'graphql/booking/queries'
import { BLOCK_OR_UNBLOCK_SEATS } from 'graphql/operations/mutations'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import loadingImage from 'assets/images/boat-loading.gif'
import { SeatBlockingLayout } from './SeatBlockingLayout'
import { VIPSeatBlockingLayout } from './VIPSeatBlockingLayout'
import { isVIPOnlyVessel, prepareStandardBoatLayout, prepareVIPOnlyBoatLayout } from 'pages/boatBooking/components/seatLayoutUtils'

export function SeatManagementModal({ 
    open, 
    onClose, 
    tripData,
    onSuccess 
}) {
    const { t } = useTranslation()
    const [selectedSeats, setSelectedSeats] = useState([])
    const [availableSeats, setAvailableSeats] = useState([])
    const [seatLayoutKey, setSeatLayoutKey] = useState('standard_vessels')
    const [seatLayout, setSeatLayout] = useState({ VIP: [], STD: [] })
    const [seatSummary, setSeatSummary] = useState([])
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [seatLoadError, setSeatLoadError] = useState(null)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [selectedWaypoints, setSelectedWaypoints] = useState({ start: null, end: null })
    const [blockLoading, setBlockLoading] = useState(false)
    const [unblockLoading, setUnblockLoading] = useState(false)
    

    // Get seat availability
    const [getBoatRouteTripSeatAvailable, { loading: seatsLoading }] = useLazyQuery(
        GET_BOAT_ROUTE_SEAT_AVAILABILITY,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getBoatRouteTripSeatAvailability.success && data.getBoatRouteTripSeatAvailability.code === '200') {
                    const layoutKey = data.getBoatRouteTripSeatAvailability.seat_layout_key || 'standard_vessels'
                    setSeatLayoutKey(layoutKey)
                    setAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                    setSeatSummary(data.getBoatRouteTripSeatAvailability.seatsummary || [])
                    prepareBoatLayout(data.getBoatRouteTripSeatAvailability.seats, layoutKey)
                    setSeatLoadError(null)
                } else {
                    setSeatLoadError(data.getBoatRouteTripSeatAvailability.message || t('Failed to load seat availability'))
                    setAvailableSeats([])
                    setSeatSummary([])
                    setSeatLayout({ VIP: [], STD: [] })
                }
            },
            onError: error => {
                setSeatLoadError(error.message || t('An error occurred while loading seats'))
                setAvailableSeats([])
                setSeatSummary([])
                setSeatLayout({ VIP: [], STD: [] })
            },
        }
    )

    // Block or unblock seats mutation
    const [blockOrUnblockSeats, { loading: mutationLoading }] = useMutation(BLOCK_OR_UNBLOCK_SEATS, {
        onCompleted: data => {
            setBlockLoading(false)
            setUnblockLoading(false)
            setSubmitLoading(false)
            if (data?.blockOrUnblockSeats?.code === '200' && data?.blockOrUnblockSeats?.success) {
                setSuccessMessage(data.blockOrUnblockSeats.message || t('Seat status updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    setShowSucDialog(false)
                    if (onSuccess) {
                        onSuccess()
                    }
                    handleClose()
                }, 1500)
            } else {
                setErrorMessage(data?.blockOrUnblockSeats?.message || t('Failed to update seat status'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setBlockLoading(false)
            setUnblockLoading(false)
            setSubmitLoading(false)
            setErrorMessage(error.message || t('An error occurred while updating seat status'))
            setShowErrDialog(true)
        },
    })

    useEffect(() => {
        if (open && tripData?.waypoints && tripData.waypoints.length > 0) {
            // Set default waypoints
            setSelectedWaypoints({
                start: tripData.waypoints[0].location_id,
                end: tripData.waypoints[tripData.waypoints.length - 1].location_id
            })
        }
    }, [open, tripData])

    useEffect(() => {
        if (selectedWaypoints.start && selectedWaypoints.end && tripData?.history_id && tripData?.activity_date) {
            fetchSeats()
        }
    }, [selectedWaypoints, tripData])

    const fetchSeats = () => {
        if (!tripData?.trip?.trip_id || !selectedWaypoints.start || !selectedWaypoints.end || !tripData?.activity_date) {
            return
        }

        getBoatRouteTripSeatAvailable({
            variables: {
                tripId: tripData.trip.trip_id,
                startLocationId: selectedWaypoints.start,
                endLocationId: selectedWaypoints.end,
                tripDate: tripData.activity_date,
            },
        })
    }

    // Check if this is a VIP-only vessel
    const isVIPOnly = isVIPOnlyVessel(seatLayoutKey)

    const prepareBoatLayout = (seats, layoutKey) => {
        // Check if this is a VIP-only vessel
        if (isVIPOnlyVessel(layoutKey)) {
            const layout = prepareVIPOnlyBoatLayout(seats, [])
            setSeatLayout(layout)
            return
        }

        // Use the standard boat layout preparation
        const layout = prepareStandardBoatLayout(seats, [], layoutKey)
        
        // Add seat_status to each seat
        const addSeatStatus = (seatArray) => {
            return seatArray.map(seat => {
                const seatNo = `${seat.row}${seat.column}`
                const originalSeat = seats.find(s => s.seat_no === seatNo)
                return {
                    ...seat,
                    seat_status: originalSeat?.seat_status || null,
                }
            })
        }

        setSeatLayout({
            VIP: addSeatStatus(layout.VIP),
            STD: addSeatStatus(layout.STD),
        })
    }

    const toggleSeatSelection = (seatNumber, seatStatus, isAvailable) => {
        // Don't allow selection of booked seats
        if (seatStatus === 'BKD' || seatStatus === 'BOOKED') {
            return
        }

        // Allow selection if seat is available OR if it is blocked (for unblocking)
        if (!isAvailable && seatStatus !== 'BLK') {
            return
        }

        setSelectedSeats(prev => {
            // If deselecting, just remove it
            if (prev.includes(seatNumber)) {
                return prev.filter(s => s !== seatNumber)
            }
            
            // If this is the first selection, allow it
            if (prev.length === 0) {
                return [...prev, seatNumber]
            }
            
            // Check if we're trying to mix blocked and available seats
            const firstSelectedSeat = availableSeats.find(seat => prev[0] === seat.seat_no)
            const firstSeatIsBlocked = firstSelectedSeat?.seat_status === 'BLK'
            const currentSeatIsBlocked = seatStatus === 'BLK'
            
            // Only allow selection if it matches the type of already selected seats
            if (firstSeatIsBlocked === currentSeatIsBlocked) {
                return [...prev, seatNumber]
            }
            
            // Don't allow mixing - seats are visually disabled, so no error needed
            return prev
        })
    }

    const handleBlockSeats = () => {
        if (selectedSeats.length === 0) {
            setErrorMessage(t('Please select at least one seat'))
            setShowErrDialog(true)
            return
        }
    
        setBlockLoading(true)
        blockOrUnblockSeats({
            variables: {
                input: {
                    action: 'BLOCK',
                    start_location_id: selectedWaypoints.start,
                    end_location_id: selectedWaypoints.end,
                    trip_date: tripData.activity_date,
                    trip_id: tripData.trip.trip_id,
                    seat_numbers: selectedSeats,
                },
            },
        })
    }
    
    const handleUnblockSeats = () => {
        if (selectedSeats.length === 0) {
            setErrorMessage(t('Please select at least one seat'))
            setShowErrDialog(true)
            return
        }
    
        setUnblockLoading(true)
        blockOrUnblockSeats({
            variables: {
                input: {
                    action: 'UNBLOCK',
                    start_location_id: selectedWaypoints.start,
                    end_location_id: selectedWaypoints.end,
                    trip_date: tripData.activity_date,
                    trip_id: tripData.trip.trip_id,
                    seat_numbers: selectedSeats,
                },
            },
        })
    }
    

    // Helper to check if selected seats are blocked or available
    const getSelectedSeatsType = () => {
        if (selectedSeats.length === 0) return null
        const firstSelectedSeat = availableSeats.find(seat => selectedSeats[0] === seat.seat_no)
        return firstSelectedSeat?.seat_status
    }

    const selectedSeatsType = getSelectedSeatsType()
    const hasBlockedSeatsSelected = selectedSeatsType === 'BLK'
    const hasAvailableSeatsSelected = selectedSeats.length > 0 && !hasBlockedSeatsSelected

    const handleClose = () => {
        setSelectedSeats([])
        setAvailableSeats([])
        setSelectedWaypoints({ start: null, end: null })
        setSeatLoadError(null)
        onClose()
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose}>
                <DialogBackdrop className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <DialogPanel className="relative transform overflow-hidden rounded-l bg-white text-left shadow-2xl transition-all w-full max-w-4xl max-h-[90vh] flex flex-col">
                            {/* Header */}
                            <div className="bg-primary px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white rounded-lg p-2">
                                        <TicketIcon className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <DialogTitle className="text-xl font-bold text-white">
                                            {t('Manage Seats')}
                                        </DialogTitle>
                                        <p className="text-sm text-white/80 mt-0.5">
                                            {t('Block or release seats for this trip')}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="rounded-lg bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Content - Two Column Layout */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="flex gap-4">
                                    {/* Left Column - Search & Status */}
                                    <div className="w-[50%] flex-shrink-0 space-y-4">
                                        {/* Waypoint Selection */}
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">{t('Route Selection')}</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                        {t('Start Location')}
                                                    </label>
                                                    <select
                                                        value={selectedWaypoints.start || ''}
                                                        onChange={(e) => setSelectedWaypoints(prev => ({ ...prev, start: e.target.value }))}
                                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm py-2"
                                                    >
                                                        <option value="">{t('Select location')}</option>
                                                        {tripData?.waypoints?.map((waypoint) => (
                                                            <option key={waypoint.location_id} value={waypoint.location_id}>
                                                                {waypoint.location?.name?.en}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                        {t('End Location')}
                                                    </label>
                                                    <select
                                                        value={selectedWaypoints.end || ''}
                                                        onChange={(e) => setSelectedWaypoints(prev => ({ ...prev, end: e.target.value }))}
                                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-sm py-2"
                                                    >
                                                        <option value="">{t('Select location')}</option>
                                                        {tripData?.waypoints?.map((waypoint) => (
                                                            <option key={waypoint.location_id} value={waypoint.location_id}>
                                                                {waypoint.location?.name?.en}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Legend */}
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">{t('Legend')}</h3>
                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                <div className="flex items-center gap-2 bg-white px-2 py-1.5 rounded border border-[#e3bf02]">
                                                    <div className="w-2.5 h-2.5 bg-white border-[1px] border-[#e3bf02] rounded"></div>
                                                    <span className="font-medium text-[#a98500]">{t('VIP')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-white px-2 py-1.5 rounded border border-teal-400">
                                                    <div className="w-2.5 h-2.5 bg-white border-[1px] border-teal-400 rounded"></div>
                                                    <span className="font-medium text-teal-800">{t('STD')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-orange-100 px-2 py-1.5 rounded border border-orange-400">
                                                    <div className="w-2.5 h-2.5 bg-orange-100 border-[1px] border-orange-400 rounded"></div>
                                                    <span className="font-medium text-orange-700">{t('Blocked')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-[#e0e8e9] px-2 py-1.5 rounded border border-gray-300">
                                                    <div className="w-2.5 h-2.5 bg-[#e0e8e9] rounded"></div>
                                                    <span className="font-medium text-gray-500">{t('Booked')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-[#128472] px-2 py-1.5 rounded col-span-2">
                                                    <CheckCircleIcon className="w-3 h-3 text-white" />
                                                    <span className="font-medium text-white">{t('Selected')}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Seat Summary */}
                                        {seatSummary && seatSummary.length > 0 && (
                                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">{t('Seat Summary')}</h3>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {seatSummary.map((summary, index) => {
                                                        let statusColor = 'gray'
                                                        if (summary.status_code === 'AVL') {
                                                            statusColor = summary.seat_type === 'VIP' ? 'yellow' : 'teal'
                                                        } else if (summary.status_code === 'BLK') {
                                                            statusColor = 'orange'
                                                        } else if ( summary.status_code === 'BKD') {
                                                            statusColor = 'gray'
                                                        }

                                                        const colorClasses = {
                                                            yellow: { bg: 'bg-[#e3bf02]/20', text: 'text-[#a98500]', badge: 'bg-[#e3bf02]', dot: 'bg-[#e3bf02]' },
                                                            teal: { bg: 'bg-teal-100', text: 'text-teal-700', badge: 'bg-teal-600', dot: 'bg-teal-600' },
                                                            orange: { bg: 'bg-orange-100', text: 'text-orange-700', badge: 'bg-orange-600', dot: 'bg-orange-600' },
                                                            gray: { bg: 'bg-gray-100', text: 'text-gray-700', badge: 'bg-gray-600', dot: 'bg-gray-600' }
                                                        }

                                                        const colors = colorClasses[statusColor]

                                                        return (
                                                            <div key={index} className={`${colors.bg} rounded-md p-2 flex items-center justify-between gap-2`}>
                                                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                                    <div className={`w-2 h-2 rounded-full ${colors.dot} flex-shrink-0`}></div>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <span className={`text-[10px] font-bold ${colors.text} uppercase leading-tight`}>{summary.seat_type}</span>
                                                                        <span className={`text-[9px] font-medium ${colors.text} opacity-75 leading-tight truncate`}>
                                                                            {summary.status_description?.en || summary.status_code}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <span className={`${colors.badge} text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0`}>
                                                                    {summary.seat_count}
                                                                </span>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Selected Seats Summary */}
                                        {selectedSeats.length > 0 && (
                                            <div className={`rounded-lg p-3 border-2 ${
                                                hasBlockedSeatsSelected 
                                                    ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300'
                                                    : 'bg-gradient-to-r from-[#128472]/10 to-[#128472]/20 border-[#128472]/50'
                                            }`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <CheckCircleIcon className={`h-4 w-4 ${hasBlockedSeatsSelected ? 'text-orange-700' : 'text-[#128472]'}`} />
                                                    <p className="text-xs font-bold text-gray-900">
                                                        {t('Selected Seats')} 
                                                        <span className={`px-1.5 py-0.5 rounded-full ml-1 text-white text-[10px] ${
                                                            hasBlockedSeatsSelected ? 'bg-orange-600' : 'bg-[#128472]'
                                                        }`}>
                                                            {selectedSeats.length}
                                                        </span>
                                                        {hasBlockedSeatsSelected && (
                                                            <span className="ml-1 text-[10px] font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                                                                {t('Blocked')}
                                                            </span>
                                                        )}
                                                        {hasAvailableSeatsSelected && (
                                                            <span className="ml-1 text-[10px] font-medium text-[#128472] bg-[#128472]/20 px-1.5 py-0.5 rounded">
                                                                {t('Available')}
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {selectedSeats.map(seat => (
                                                        <span
                                                            key={seat}
                                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-white border-2 shadow-sm ${
                                                                hasBlockedSeatsSelected 
                                                                    ? 'border-orange-600 text-orange-700'
                                                                    : 'border-[#128472] text-[#128472]'
                                                            }`}
                                                        >
                                                            {seat}
                                                            <button
                                                                onClick={() => setSelectedSeats(prev => prev.filter(s => s !== seat))}
                                                                className="hover:bg-red-50 rounded-full"
                                                            >
                                                                <XMarkIcon className="h-3 w-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right Column - Boat Layout */}
                                    <div className="flex-1 min-w-0">
                                        {seatsLoading ? (
                                            <div className="flex flex-col justify-center items-center py-12 h-full bg-gray-50 rounded-lg">
                                                <img alt="loading" src={loadingImage} className="w-24" />
                                                <p className="pt-3 text-primary font-semibold text-sm">{t('Loading seats')}...</p>
                                            </div>
                                        ) : seatLoadError ? (
                                            <div className="flex flex-col justify-center items-center py-12 h-full bg-red-50 rounded-lg border-2 border-red-200">
                                                <div className="bg-red-100 rounded-full p-4 mb-4">
                                                    <XMarkIcon className="h-12 w-12 text-red-600" />
                                                </div>
                                                <p className="text-red-800 font-semibold text-base mb-2">{t('Unable to Load Seats')}</p>
                                                <p className="text-red-600 text-sm text-center max-w-md px-4">{seatLoadError}</p>
                                            </div>
                                        ) : availableSeats.length === 0 ? (
                                            <div className="flex flex-col justify-center items-center py-12 h-full bg-gray-50 rounded-lg border-2 border-gray-200">
                                                <div className="bg-gray-100 rounded-full p-4 mb-4">
                                                    <TicketIcon className="h-12 w-12 text-gray-400" />
                                                </div>
                                                <p className="text-gray-600 font-semibold text-base">{t('No Seats Available')}</p>
                                                <p className="text-gray-500 text-sm mt-1">{t('Please select a different route')}</p>
                                            </div>
                                        ) : (
                                            <div className="h-full overflow-auto">
                                                {isVIPOnly ? (
                                                    <VIPSeatBlockingLayout
                                                        seatLayout={seatLayout}
                                                        selectedSeats={selectedSeats}
                                                        onSeatToggle={toggleSeatSelection}
                                                        availableSeats={availableSeats}
                                                    />
                                                ) : (
                                                    <SeatBlockingLayout
                                                        seatLayout={seatLayout}
                                                        selectedSeats={selectedSeats}
                                                        onSeatToggle={toggleSeatSelection}
                                                        seatLayoutKey={seatLayoutKey}
                                                        availableSeats={availableSeats}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex gap-3 justify-end">
                            <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={submitLoading}
                                    className="rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    {t('Cancel')}
                                </button>
                            <button
                                type="button"
                                onClick={handleBlockSeats}
                                disabled={!hasAvailableSeatsSelected || blockLoading}
                                className="rounded-lg bg-red px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {blockLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        {t('Processing...')}
                                    </>
                                ) : (
                                    <>
                                        <LockClosedIcon className="h-5 w-5" />
                                        {t('Block Seats')}
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={handleUnblockSeats}
                                disabled={!hasBlockedSeatsSelected || unblockLoading}
                                className="rounded-lg bg-[#128472] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0f6d5f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                {unblockLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        {t('Processing...')}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleIcon className="h-5 w-5" />
                                        {t('Release Seats')}
                                    </>
                                )}
                            </button>

                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={handleErrClose}
                />
            )}

            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => setShowSucDialog(false)}
                />
            )}
        </>
    )
}
